import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AddinClientService } from '@blackbaud/skyux-lib-addin-client';
import { AddinClientInitArgs } from '@blackbaud/sky-addin-client';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, debounceTime, exhaustMap, map, switchMap, take, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import {
  DonorAtlasService,
  DonorAtlasSummary,
  DonorSearchResult,
  EmbedDataResponse
} from './donoratlas.service';
import { SpinningLogoComponent } from './spinning-logo.component';

/** sessionStorage key prefix for cached constituent summary. */
const SUMMARY_CACHE_PREFIX = 'donoratlas_summary_';

/** sessionStorage key prefix for cached embed data (URL + donor). */
const EMBED_CACHE_PREFIX = 'donoratlas_embed_';

/** Status polling: first read soon after a change, then a steady cadence. */
const POLL_FIRST_DELAY_MS = 2000;
const POLL_INTERVAL_MS = 5000;

/** Consecutive failed polls before the tile stops and reports it. */
const POLL_MAX_FAILURES = 3;

/**
 * How long after a mutation's 202 an idle status is not believed. The
 * job the backend accepted may not be visible to the status route on
 * the very next read, so the tile keeps its buttons gated and polls
 * until either the job shows up (after which `processing` is trusted)
 * or this window passes.
 */
const MUTATION_SETTLE_MS = 30000;

/** The record mutations a user can start from the tile. */
export type MutationKind = 'sync' | 'resync' | 'unsync' | 'match';

/** A 403 whose detail is our permission denial (see integrations_layer.permissions). */
export function isPermissionDenied(error: any): boolean {
  return !!(
    error &&
    error.status === 403 &&
    typeof error.error?.detail === 'string' &&
    error.error.detail.startsWith('Permission denied:')
  );
}

/** The backend's own sentence for a permission denial, verbatim. */
export function permissionDeniedMessage(error: any): string {
  return String(error.error.detail).replace(/^Permission denied:\s*/, '');
}

const CONNECTOR_UNREACHABLE_MESSAGE =
  'Unable to reach the DonorAtlas connector. Please verify that the integration is configured and accessible.';

const BLACKBAUD_AUTH_FAILED_MESSAGE =
  'Failed to authenticate with Blackbaud. Please refresh and try again.';

@Component({
  selector: 'app-donoratlas-addin',
  imports: [CommonModule, SpinningLogoComponent],
  templateUrl: './donoratlas-addin.html',
  styleUrl: './donoratlas-addin.scss'
})
export class DonorAtlasTileComponent implements OnInit, OnDestroy {
  public environmentId: string | undefined;
  public constituentId: string | undefined;
  public userIdentityToken: string | undefined;

  public loading = true;
  public errorMessage: string | null = null;

  public summary: DonorAtlasSummary | null = null;

  public profileLoading = false;
  public profileError: string | null = null;

  /** DonorAtlas setup page for this environment; the not-configured state links here. */
  public readonly setupUrl = `${environment.donoratlasFrontendUrl}/integrations/re/setup/configure`;

  /**
   * The mutation this tile started and has not yet seen finish. Set on
   * click, kept through the 202 and the job that follows, and cleared
   * only when a status read reports the record idle (see
   * `applySummary`) or the request itself fails. While set, every
   * action button is disabled.
   */
  public activeMutation: MutationKind | null = null;

  /**
   * Epoch ms before which an idle status is not trusted to release
   * `activeMutation`. Set on every 202; zeroed as soon as a status read
   * shows the job running.
   */
  private idleTrustedAfter = 0;

  // Manual-match picker (no-match and not-yet-synced states). One
  // input serves both a name search and a pasted profile URL; the
  // backend's /donors/search resolves either.
  public matchQuery = '';
  public matchResults: DonorSearchResult[] = [];
  public matchSelection: DonorSearchResult | null = null;
  public matchSearchLoading = false;
  public matchError: string | null = null;
  private readonly matchQuery$ = new Subject<string>();
  private matchSearchSubscription: Subscription | null = null;

  /** Donor ids whose photo failed to load; they render initials instead. */
  private readonly failedPhotos = new Set<string>();

  private _embedUrl: string | null = null;
  private _safeEmbedUrl: SafeResourceUrl | null = null;
  private embedDonorData: Record<string, any> | null = null;

  private pollingSubscription: Subscription | null = null;

  /**
   * Consecutive failed poll ticks. A component field rather than a
   * closure inside `startPolling` so that a successful status read from
   * *any* path (an out-of-band `loadSummary` landing in `applySummary`
   * between poll ticks, not just a poll's own success) clears the
   * strike count; the tile just saw a healthy backend, so the next poll
   * failure should be strike one, not the "Lost contact" bailout.
   */
  private pollFailures = 0;

  private messageListener: ((event: MessageEvent) => void) | null = null;
  private profileLoadingTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly addinClientService: AddinClientService,
    private readonly donorAtlasService: DonorAtlasService,
    private readonly sanitizer: DomSanitizer
  ) { }

  /**
   * Set the embed URL and update the cached SafeResourceUrl.
   *
   * We cache the sanitized URL so the same object reference is returned
   * on every change-detection cycle. Without caching,
   * bypassSecurityTrustResourceUrl() returns a new object each time,
   * Angular sees a "changed" [src] binding, and reloads the iframe in
   * an infinite loop.
   */
  public set embedUrl(url: string | null) {
    if (url === this._embedUrl) return;
    this._embedUrl = url;
    this._safeEmbedUrl = url
      ? this.sanitizer.bypassSecurityTrustResourceUrl(url)
      : null;
  }

  public get embedUrl(): string | null {
    return this._embedUrl;
  }

  /**
   * Get a sanitized embed URL safe for use in iframe [src] binding.
   * Returns a cached reference to avoid triggering iframe reloads on
   * every Angular change-detection cycle.
   */
  get safeEmbedUrl(): SafeResourceUrl | null {
    return this._safeEmbedUrl;
  }

  public ngOnInit(): void {
    // Debounced manual-match search. switchMap drops stale requests
    // when the user keeps typing. Every debounced query is searched,
    // even a repeat of the last one: onMatchQueryChange flips the
    // loading flag before emitting, so a suppressed emission would
    // leave "Searching…" up for good.
    this.matchSearchSubscription = this.matchQuery$
      .pipe(
        debounceTime(300),
        switchMap((query) =>
          this.donorAtlasService.searchDonors(query).pipe(
            map((response) => ({ results: response.results ?? [], error: null as string | null })),
            catchError((error) =>
              of({
                results: [] as DonorSearchResult[],
                error: isPermissionDenied(error) ? permissionDeniedMessage(error) : 'Unable to search DonorAtlas.'
              })
            )
          )
        )
      )
      .subscribe(({ results, error }) => {
        this.matchSearchLoading = false;
        this.matchResults = results;
        this.matchError = error;
      });

    this.addinClientService.args.pipe(take(1)).subscribe((args: AddinClientInitArgs) => {
      this.environmentId = args.envId;
      this.constituentId = args.context.recordId;

      // Restore cached state so the tile renders instantly on iframe
      // recreation (e.g. Blackbaud destroys/recreates on window resize).
      const hasCachedState = this.restoreFromCache();


      args.ready({
        showUI: true,
        title: environment.envName ? `DonorAtlas (${environment.envName})` : 'DonorAtlas',
        // Drops the SKY tile shell's 12px content padding-top (its 12px
        // margin-top stays); the tile card's own 24px still insets the sides.
        tileConfig: { removeInset: true }
      });

      // Get user identity token first, then load summary
      this.getUserIdentityToken(() => {
        if (this.environmentId && this.constituentId) {
          // If we restored cached state, refresh silently in the background
          // so the user never sees a loading spinner on resize.
          this.loadSummary(hasCachedState);
        } else {
          this.loading = false;
          this.errorMessage =
            'Unable to determine Blackbaud environment or constituent ID from the add-in context.';
        }
      });
    });
  }

  /** Organization constituent pages get a placeholder; orgs can't sync. */
  public isOrgPage(): boolean {
    return this.summary?.constituent_type === 'Organization';
  }

  /** Sticky per-load failure flag so a broken entity logo never flickers
   *  back in on the next status poll. */
  public orgLogoFailed = false;

  /**
   * Whether this user's DonorAtlas team lets them perform ``permission``
   * ('sync' | 'unsync') through the integration. Viewing is never gated:
   * it comes with the connection, so a user with neither is view-only.
   * A status from a backend that predates permissions carries none;
   * treat that as everything. Only decides what to render; the backend
   * enforces the same set.
   */
  public hasPermission(permission: 'sync' | 'unsync'): boolean {
    const granted = this.summary?.permissions;
    return granted === undefined || granted === null || granted.includes(permission);
  }

  public get canSync(): boolean {
    return this.hasPermission('sync');
  }

  public get canUnsync(): boolean {
    return this.hasPermission('unsync');
  }

  /**
   * One line explaining what this user can't do, for the states that
   * would otherwise show the missing button. Null when nothing is
   * restricted.
   */
  public get permissionHint(): string | null {
    const missing: string[] = [];
    if (!this.canSync) {
      missing.push('sync');
    }
    if (!this.canUnsync) {
      missing.push('unsync');
    }
    if (missing.length === 0) {
      return null;
    }
    const what = missing.length === 2 ? 'Syncing and unsyncing are' : `${missing[0] === 'sync' ? 'Syncing' : 'Unsyncing'} is`;
    return `${what} turned off for your account. A DonorAtlas team admin can switch it on under Integrations → Users & connections.`;
  }

  /**
   * Fetch the constituent's sync status from the API.
   *
   * Parameters
   * ----------
   * silent : boolean
   *     When true, skip setting ``loading = true`` so the UI keeps showing
   *     its current (cached) state while the API call runs in the background.
   *     Defaults to false (shows the loading spinner).
   */
  private loadSummary(silent: boolean = false): void {
    if (!this.environmentId || !this.constituentId) {
      return;
    }

    if (!silent) {
      this.loading = true;
      // Only wipe prior error context on a user-initiated (non-silent)
      // refresh. In silent mode the user hasn't asked for anything visible,
      // so any pre-existing error stays put until either the silent call
      // succeeds (cleared in applySummary) or the user explicitly retries.
      this.errorMessage = null;
    }

    this.donorAtlasService
      .getConstituentStatus(this.constituentId)
      .subscribe({
        next: (summary) => this.applySummary(summary),
        error: (error) => {
          this.loading = false;

          if (this.activeMutation !== null) {
            // A job this tile started may be running. Keep the buttons
            // gated and let polling find out; it releases and reports
            // if the backend stays unreachable.
            this.startPolling();
          } else {
            this.stopPolling();
          }

          // On silent refresh failure, keep cached state visible; only
          // surface errors when the user has no cached state to fall back on.
          if (silent && this.summary) {
            if (!environment.production) {
              console.error('Silent refresh failed, keeping cached state:', error);
            }
            return;
          }

          if (error && error.status === 0) {
            this.errorMessage = CONNECTOR_UNREACHABLE_MESSAGE;
          } else {
            this.errorMessage = 'Unable to load DonorAtlas status for this constituent.';
          }
          if (!environment.production) {
            console.error(error);
          }
        }
      });
  }

  /**
   * Make a freshly read status the tile's state. Every status read,
   * whether a direct fetch or a poll, lands here so the synced
   * transition, the polling decision and the release of
   * `activeMutation` behave identically.
   */
  private applySummary(summary: DonorAtlasSummary): void {
    this.summary = summary;
    // A wall that lands mid-flow must not strand a half-driven picker
    // under a disabled input: drop the query and any un-confirmed
    // selection. (matchSubmitting flows are already in flight and
    // resolve on their own.)
    if (summary?.sky_quota_exhausted === true && this.activeMutation !== 'match') {
      this.matchQuery = '';
      this.matchResults = [];
      this.matchSelection = null;
    }
    this.loading = false;
    // Good data invalidates any stale error banner, so a silent recovery
    // wipes the prior failure. Same for the poll strike count: a status
    // that landed here came from a working backend, whichever request
    // fetched it.
    this.pollFailures = 0;
    this.errorMessage = null;

    // Persist to sessionStorage so the next iframe recreation renders
    // instantly without a loading flash.
    this.cacheSummary(this.constituentId!, summary);

    // If the constituent is no longer synced (e.g. external unsync,
    // post-resync no-match), drop the stale embed state. Without
    // this, a subsequent re-sync would short-circuit on the
    // !this.embedUrl guard below and render the previous iframe.
    this.clearEmbedStateIfStale(summary);

    if (summary.processing) {
      // The job is visible; from here on an idle status means it finished.
      this.idleTrustedAfter = 0;
    }
    const idle = !summary.processing && Date.now() >= this.idleTrustedAfter;
    if (idle) {
      this.activeMutation = null;
      this.stopPolling();
    } else {
      this.startPolling();
    }

    // Kick off the profile iframe load as soon as we know the donor
    // is synced. The iframe lives directly in the synced layout (no
    // toggle), so this fetch IS the user-visible load: failures
    // surface as the profile error + Retry, not as silent state.
    if (summary.profile_url && !this.embedUrl && !this.profileError) {
      this.loadProfile();
    }
  }

  /**
   * Poll the status until `applySummary` decides the record is idle.
   * Each poll fetches a fresh Blackbaud identity token first: tokens
   * live about five minutes and a job can easily outlive one. A poll
   * that fails is skipped; POLL_MAX_FAILURES in a row stop polling,
   * release the in-flight mutation and say so.
   */
  private startPolling(): void {
    // Don't start multiple polling subscriptions
    if (this.pollingSubscription) {
      return;
    }

    this.pollFailures = 0;
    // A first check after 2s, then every 5s: a single-record write-back
    // often finishes in seconds, and a long first interval is pure
    // display lag between the job completing and the tile saying so.
    // exhaustMap lets a slow read finish instead of cancelling it on
    // the next tick.
    this.pollingSubscription = timer(POLL_FIRST_DELAY_MS, POLL_INTERVAL_MS)
      .pipe(
        exhaustMap(() =>
          this.freshIdentityToken$().pipe(
            switchMap(() => this.donorAtlasService.getConstituentStatus(this.constituentId!)),
            catchError((error) => {
              if (!environment.production) {
                console.error('Polling error:', error);
              }
              return of(null);
            })
          )
        )
      )
      .subscribe((summary) => {
        if (summary === null) {
          this.pollFailures += 1;
          if (this.pollFailures >= POLL_MAX_FAILURES) {
            this.stopPolling();
            this.activeMutation = null;
            this.errorMessage = 'Lost contact with DonorAtlas while a job was running. Refresh to check its status.';
          }
          return;
        }
        // applySummary resets pollFailures alongside the error banner.
        this.applySummary(summary);
      });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  public ngOnDestroy(): void {
    this.stopPolling();
    this.resetProfileState();
    if (this.matchSearchSubscription) {
      this.matchSearchSubscription.unsubscribe();
      this.matchSearchSubscription = null;
    }
  }

  public onConfigureClick(): void {
    // Open the DonorAtlas setup page to link this environment to a team
    const baseUrl = `${environment.donoratlasFrontendUrl}/integrations/re/setup`;


    window.open(baseUrl, '_blank', 'noopener,noreferrer,width=800,height=600');
  }

  /**
   * Run one record mutation end to end: gate the buttons, refresh the
   * short-lived Blackbaud token (a tile that has sat idle would 401 at
   * the gateway), send the request, and on its 202 reload the status
   * and keep the buttons gated until the job it queued is seen to
   * finish. Errors share one ladder, so every flow handles connector
   * outages, exhausted imports, 409s and permission denials the same way.
   *
   * Parameters
   * ----------
   * kind : MutationKind
   *     Which flow this is; decides which error slot every failure
   *     writes, the token fetch included (`matchError` for the picker,
   *     `errorMessage` otherwise).
   * request : () => Observable<unknown>
   *     The API call. Only subscribed once the token is fresh.
   * failureMessage : string
   *     Shown when no more specific case applies.
   * onAccepted : () => void, optional
   *     Side effects to run on the 202, before the status reload.
   */
  private runMutation(spec: {
    kind: MutationKind;
    request: () => Observable<unknown>;
    failureMessage: string;
    onAccepted?: () => void;
  }): void {
    if (!this.constituentId || this.activeMutation !== null) {
      return;
    }
    this.activeMutation = spec.kind;
    this.setMutationError(spec.kind, null);

    this.freshIdentityToken$().subscribe({
      next: () => {
        spec.request().subscribe({
          next: () => {
            this.idleTrustedAfter = Date.now() + MUTATION_SETTLE_MS;
            spec.onAccepted?.();
            // Silent: the current layout stays put (the synced iframe
            // included) while the reload fetches the processing state.
            // applySummary keeps polling until the job is seen to end.
            this.loadSummary(true);
          },
          error: (error) => {
            this.activeMutation = null;
            if (error && error.status === 0) {
              this.setMutationError(spec.kind, CONNECTOR_UNREACHABLE_MESSAGE);
            } else if (error && error.status === 402) {
              this.setMutationError(spec.kind, 'No imports remaining. Ask your team admin to add more.');
            } else if (error && error.status === 409) {
              // The record changed under us (someone synced it, or
              // another operation holds it); the reloaded state
              // explains itself. Only the match flow owns the picker,
              // so only it may clear an in-progress search there.
              if (spec.kind === 'match') {
                this.resetMatchPicker();
              }
              this.loadSummary(true);
            } else if (isPermissionDenied(error)) {
              this.setMutationError(spec.kind, permissionDeniedMessage(error));
              // The status now knows; the reload hides the button.
              this.loadSummary(true);
            } else {
              this.setMutationError(spec.kind, spec.failureMessage);
            }
            if (!environment.production) {
              console.error(error);
            }
          }
        });
      },
      error: (error) => {
        // The kind-based slot routing covers the token step too: an
        // auth failure on a match belongs inside the picker, not the
        // tile banner.
        this.activeMutation = null;
        this.setMutationError(spec.kind, BLACKBAUD_AUTH_FAILED_MESSAGE);
        if (!environment.production) {
          console.error('Failed to retrieve user identity token.', error);
        }
      }
    });
  }

  /** The picker owns its own error line; every other flow uses the tile banner. */
  private setMutationError(kind: MutationKind, message: string | null): void {
    if (kind === 'match') {
      this.matchError = message;
    } else {
      this.errorMessage = message;
    }
  }

  public onSyncClick(): void {
    this.runMutation({
      kind: 'sync',
      request: () => this.donorAtlasService.startSync(this.constituentId!),
      failureMessage: 'Unable to start DonorAtlas sync.'
    });
  }

  /**
   * Resync is a background refresh: the live embed iframe stays up
   * showing pre-resync data until something else (unsync, navigation,
   * page reload) tears it down, rather than flashing a spinner over the
   * profile the user is looking at. The sessionStorage cache is dropped
   * so a Blackbaud-driven iframe remount fetches fresh data instead of
   * restoring the stale copy.
   */
  public onResyncClick(): void {
    this.runMutation({
      kind: 'resync',
      request: () => this.donorAtlasService.resyncConstituent(this.constituentId!),
      failureMessage: 'Unable to resync constituent.',
      onAccepted: () => this.clearCache(this.constituentId!)
    });
  }

  /**
   * Unsync runs as a job too: the record stays synced (and the profile
   * stays on screen, with the toolbar reading "Unsyncing…") until the
   * job removes the row, at which point the stale-embed teardown in
   * `applySummary` clears the iframe and its cache.
   */
  public onUnsyncClick(): void {
    this.runMutation({
      kind: 'unsync',
      request: () => this.donorAtlasService.unsyncConstituent(this.constituentId!),
      failureMessage: 'Unable to unsync constituent.'
    });
  }

  public onMatchConfirm(): void {
    const selection = this.matchSelection;
    if (!selection) {
      return;
    }
    this.runMutation({
      kind: 'match',
      request: () => this.donorAtlasService.manualMatchConstituent(this.constituentId!, selection.donor_id),
      failureMessage: 'Unable to match this constituent.',
      onAccepted: () => this.resetMatchPicker()
    });
  }

  public onMatchQueryChange(value: string): void {
    this.matchQuery = value;
    this.matchSelection = null;
    this.matchError = null;
    const query = value.trim();
    if (query.length < 2) {
      this.matchResults = [];
      this.matchSearchLoading = false;
      return;
    }
    this.matchSearchLoading = true;
    this.matchQuery$.next(query);
  }

  public onMatchSelect(result: DonorSearchResult): void {
    this.matchSelection = result;
    this.matchError = null;
  }

  public onMatchClearSelection(): void {
    this.matchSelection = null;
  }

  private resetMatchPicker(): void {
    this.matchQuery = '';
    this.matchResults = [];
    this.matchSelection = null;
    this.matchError = null;
    this.matchSearchLoading = false;
  }

  public onViewProfileClick(): void {
    if (this.summary?.profile_url) {
      window.open(this.summary.profile_url, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Retry loading the donor profile after an error.
   * Resets error state and fetches a fresh embed URL.
   */
  public retryProfile(): void {
    this.profileError = null;
    this.embedUrl = null;
    this.loadProfile();
  }

  /**
   * Fetch a fresh embed URL and surface the profile in the iframe.
   *
   * Called from applySummary once we know the donor is synced, and from
   * retryProfile() after an error. Sets profileLoading=true so the tile
   * shows the spinner overlay until the iframe signals donoratlas:loaded
   * (or the 15s fallback fires). The iframe is mounted as soon as the URL
   * is set and stays hidden behind the spinner until then.
   */
  private loadProfile(): void {
    if (!this.constituentId) return;

    this.profileLoading = true;
    this.profileError = null;

    // Refresh the Blackbaud JWT before calling the embed data endpoint.
    // The token is short-lived (5 minutes) and may have expired since page load.
    // We pass an errorCallback so a token-refresh failure clears the profile
    // spinner and surfaces a retry button; otherwise getUserIdentityToken's
    // error path only touches errorMessage/loading and leaves profileLoading
    // stuck true forever.
    this.getUserIdentityToken(
      () => {
        this.donorAtlasService.getEmbedData(this.constituentId!).subscribe({
          next: (response) => {
            this.embedDonorData = response.donor ?? null;
            // Set up the postMessage listener BEFORE setting embedUrl so the
            // listener is ready when the iframe sends donoratlas:ready.
            this.setupPostMessageListener();
            this.embedUrl = response.embed_url;

            // Cache embed data so iframe recreation doesn't need to re-fetch
            this.cacheEmbedData(this.constituentId!, response);

            // Fallback: clear loading after 15s even if no donoratlas:loaded
            // postMessage is received from the embedded page (e.g. JS error,
            // network issue). The timeout is cleaned up on destroy/reset via
            // clearProfileLoadingTimeout().
            this.clearProfileLoadingTimeout();
            this.profileLoadingTimeoutId = setTimeout(() => {
              if (this.profileLoading) {
                this.profileLoading = false;
              }
              this.profileLoadingTimeoutId = null;
            }, 15000);
          },
          error: (err) => {
            this.profileError = 'Unable to load profile. Please try again.';
            this.profileLoading = false;
            if (!environment.production) {
              console.error('Error fetching embed data:', err);
            }
          },
        });
      },
      () => {
        // Token refresh failed. getUserIdentityToken already set the top-level
        // errorMessage; we just need to free the profile area so the user
        // sees a retry button instead of an infinite spinner.
        this.profileLoading = false;
        this.profileError = 'Unable to load profile. Please try again.';
      }
    );
  }

  /**
   * Set up a listener for postMessage events from the embed iframe.
   * Handles the "loaded" signal so the tile can dismiss the loading spinner.
   */
  private setupPostMessageListener(): void {
    // Clean up any existing listener
    this.cleanupPostMessageListener();

    this.messageListener = (event: MessageEvent) => {
      if (!event.data?.type) return;

      // Validate that the message originates from the embed iframe's domain
      if (this.embedUrl) {
        try {
          const expectedOrigin = new URL(this.embedUrl).origin;
          if (event.origin !== expectedOrigin) return;
        } catch {
          return;
        }
      }

      switch (event.data.type) {
        case 'donoratlas:ready': {
          // Iframe is ready to receive donor data via postMessage
          const iframe = document.querySelector<HTMLIFrameElement>('.donoratlas-profile-iframe');
          if (iframe?.contentWindow && this.embedDonorData && this.embedUrl) {
            try {
              const targetOrigin = new URL(this.embedUrl).origin;
              iframe.contentWindow.postMessage(
                { type: 'donoratlas:data', donor: this.embedDonorData },
                targetOrigin
              );
            } catch { /* ignore URL parse errors */ }
          }
          break;
        }
        case 'donoratlas:loaded':
          this.profileLoading = false;
          this.clearProfileLoadingTimeout();
          break;
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Drop in-memory + sessionStorage embed state when the constituent
   * transitions out of a synced state (profile_url goes falsy while
   * embedUrl is still populated from a prior sync).
   *
   * Without this, a subsequent re-sync hits the ``!this.embedUrl`` guard
   * in applySummary, skips loadProfile(), and renders the stale iframe.
   * We also drop the embed sessionStorage entry so a Blackbaud-driven
   * iframe remount (e.g. window resize) doesn't restore the stale URL
   * via restoreFromCache().
   *
   * Parameters
   * ----------
   * summary : DonorAtlasSummary
   *     The newly-arrived summary. No-op unless ``profile_url`` is falsy
   *     and we have a leftover embed URL to clear.
   */
  private clearEmbedStateIfStale(summary: DonorAtlasSummary): void {
    if (summary.profile_url || !this.embedUrl) {
      return;
    }
    this.resetProfileState();
    if (this.constituentId) {
      try {
        sessionStorage.removeItem(EMBED_CACHE_PREFIX + this.constituentId);
      } catch {
        // sessionStorage may be unavailable — non-critical
      }
    }
  }

  /**
   * Reset all profile-related state and clean up listeners/timers.
   *
   * Called when the profile iframe is being torn down (unsync, resync,
   * component destroy) to ensure no stale state persists.
   */
  private resetProfileState(): void {
    this.embedUrl = null;
    this.embedDonorData = null;
    this.profileError = null;
    this.profileLoading = false;
    this.cleanupPostMessageListener();
    this.clearProfileLoadingTimeout();
  }

  private cleanupPostMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }

  private clearProfileLoadingTimeout(): void {
    if (this.profileLoadingTimeoutId !== null) {
      clearTimeout(this.profileLoadingTimeoutId);
      this.profileLoadingTimeoutId = null;
    }
  }

  // ---------------------------------------------------------------------------
  // sessionStorage cache — survives Blackbaud iframe destroy/recreate but
  // not tab close, keeping data scoped to the current browsing session.
  // ---------------------------------------------------------------------------

  /**
   * Restore summary and embed data from sessionStorage.
   *
   * Returns
   * -------
   * boolean
   *     True if cached state was restored (caller should use silent refresh).
   */
  private restoreFromCache(): boolean {
    if (!this.constituentId) return false;

    let restored = false;

    // Restore summary
    try {
      const raw = sessionStorage.getItem(SUMMARY_CACHE_PREFIX + this.constituentId);
      if (raw) {
        const cached: DonorAtlasSummary = JSON.parse(raw);
        this.summary = cached;
        this.loading = false;
        restored = true;
      }
    } catch {
      // Corrupted cache — ignore and let the API call populate fresh data
    }

    // Restore embed data
    try {
      const raw = sessionStorage.getItem(EMBED_CACHE_PREFIX + this.constituentId);
      if (raw) {
        const cached: { embed_url: string; donor: Record<string, any> | null } = JSON.parse(raw);
        this.embedDonorData = cached.donor;
        this.setupPostMessageListener();
        this.embedUrl = cached.embed_url;
        restored = true;
      }
    } catch {
      // Corrupted cache — ignore
    }

    return restored;
  }

  /**
   * Persist the constituent summary to sessionStorage.
   */
  private cacheSummary(constituentId: string, summary: DonorAtlasSummary): void {
    try {
      sessionStorage.setItem(
        SUMMARY_CACHE_PREFIX + constituentId,
        JSON.stringify(summary)
      );
    } catch {
      // sessionStorage full or unavailable — non-critical, skip silently
    }
  }

  /**
   * Persist embed data (URL and donor payload) to sessionStorage.
   */
  private cacheEmbedData(constituentId: string, response: EmbedDataResponse): void {
    try {
      sessionStorage.setItem(
        EMBED_CACHE_PREFIX + constituentId,
        JSON.stringify({ embed_url: response.embed_url, donor: response.donor ?? null })
      );
    } catch {
      // sessionStorage full or unavailable — non-critical, skip silently
    }
  }

  /**
   * Clear cached data for a constituent (e.g. on unsync).
   */
  private clearCache(constituentId: string): void {
    try {
      sessionStorage.removeItem(SUMMARY_CACHE_PREFIX + constituentId);
      sessionStorage.removeItem(EMBED_CACHE_PREFIX + constituentId);
    } catch {
      // Ignore — sessionStorage may be unavailable
    }
  }

  /** "Resynced" when the last stamp came from a resync, else "Synced"
   *  (including rows stamped before last_synced_op existed). */
  public get syncVerb(): string {
    return this.summary?.last_synced_op === 'resync' ? 'Resynced' : 'Synced';
  }

  /** "Synced 3 minutes ago by Jane Doe", or null before the first stamp. */
  public get syncStamp(): string | null {
    const s = this.summary;
    if (!s?.last_synced_at) {
      return null;
    }
    const by = s.last_synced_by ? ` by ${s.last_synced_by}` : '';
    return `${this.syncVerb} ${this.formatLastSynced(s.last_synced_at)}${by}`;
  }

  public formatLastSynced(epochTime: number | undefined): string {
    if (!epochTime) {
      return '';
    }

    const date = new Date(epochTime * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  /**
   * Percent complete (0-100) for the in-flight backend job, or null when
   * there is no determinate progress (not processing, no progress/total
   * fields, or the plan has not published total yet). When null, the
   * template shows the processing pill without a bar.
   */
  public get processingPercent(): number | null {
    const s = this.summary;
    if (!s?.processing) {
      return null;
    }
    if (s.progress == null || s.total == null || s.total <= 0) {
      return null;
    }
    return Math.max(0, Math.min(100, Math.round((s.progress / s.total) * 100)));
  }

  /**
   * True while any mutation is running against this record: a click
   * this tile made and has not seen finish, or a backend job the status
   * endpoint reports via the processing flag. Disables every action
   * button so, for example, a resync cannot start mid-unsync.
   */
  public get mutationInFlight(): boolean {
    return this.activeMutation !== null || !!this.summary?.processing;
  }

  /**
   * The team's SKY daily quota is spent (per the last status read), so
   * every mutation this tile could start would just queue behind a wall
   * that stands for hours. Buttons disable with quotaTitle as the hover
   * explanation. Absent on older backends → false → buttons stay live.
   */
  public get quotaExhausted(): boolean {
    return this.summary?.sky_quota_exhausted === true;
  }

  /**
   * The notice rendered in place of the action buttons and the
   * manual-match panel while the wall stands. Deliberately the
   * backend's own sentence (the one the app and the 429s show), so
   * every surface phrases the wall identically and the horizon is
   * always the product's, never a tile-side recomputation that can
   * lag a deploy behind it.
   */
  public get quotaTitle(): string {
    return (
      this.summary?.quota_message ??
      "You've hit your team's daily Raiser's Edge NXT limit — try again after it resets."
    );
  }

  /** The Sync / Resync / "Try match again" click is still in flight. */
  public get enhancing(): boolean {
    return this.activeMutation === 'sync' || this.activeMutation === 'resync';
  }

  public get unsyncing(): boolean {
    return this.activeMutation === 'unsync';
  }

  public get matchSubmitting(): boolean {
    return this.activeMutation === 'match';
  }

  /**
   * "Syncing…" / "Resyncing…" / "Unsyncing…" for the job the status
   * reports running, from its job type alone; null when idle. No item
   * counts: a single constituent's job counts internal item writes,
   * which mean nothing to a user.
   */
  public get processingVerbLabel(): string | null {
    const s = this.summary;
    if (!s?.processing) {
      return null;
    }
    if (s.active_job_type === 'integration_unsync') {
      return 'Unsyncing…';
    }
    if (s.active_job_type === 'integration_resync') {
      return 'Resyncing…';
    }
    return 'Syncing…';
  }

  /** Two-letter initials for the avatar fallback when a donor has no photo. */
  public initialsFor(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  }

  /** Whether to render the donor's photo rather than initials. */
  public hasPhoto(donor: DonorSearchResult): boolean {
    return !!donor.photo_url && !this.failedPhotos.has(donor.donor_id);
  }

  /** A photo URL that 404s falls back to the initials avatar. */
  public onPhotoError(donor: DonorSearchResult): void {
    this.failedPhotos.add(donor.donor_id);
  }

  public isNoMatchFound(): boolean {
    return !!(this.summary &&
      !this.summary.profile_url &&
      !this.summary.processing &&
      this.summary.last_synced_at &&
      this.summary.message === 'No match found');
  }

  public isNotSyncedYet(): boolean {
    return !!(this.summary &&
      !this.summary.profile_url &&
      !this.summary.processing &&
      !this.summary.last_synced_at);
  }

  /** A fresh Blackbaud identity token, stored for the API service as a side effect. */
  private freshIdentityToken$(): Observable<string> {
    return this.addinClientService.getUserIdentityToken().pipe(
      tap((token: string) => {
        this.userIdentityToken = token;
        this.donorAtlasService.setUserIdentityToken(token);
      })
    );
  }

  private getUserIdentityToken(callback?: () => void, errorCallback?: () => void): void {
    this.userIdentityToken = undefined;

    this.freshIdentityToken$().subscribe({
      next: () => {
        // Execute callback if provided (e.g., to load summary after token is set)
        if (callback) {
          callback();
        }
      },
      error: (error) => {
        if (!environment.production) {
          console.error('Failed to retrieve user identity token.', error);
        }
        this.errorMessage = BLACKBAUD_AUTH_FAILED_MESSAGE;
        this.loading = false;
        // Let the caller clean up its own loading state (e.g. profileLoading)
        // so a token failure mid-loadProfile doesn't leave the embed area
        // spinning forever.
        if (errorCallback) {
          errorCallback();
        }
      }
    });
  }
}
